import { useState, useRef, useEffect } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus,
  Trash2,
  Upload,
  FileCheck,
  AlertTriangle,
  History,
  Shield,
  Building2,
  Search,
  Check,
  X,
  Loader2,
} from "lucide-react";
import { format, differenceInDays, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { useLocation } from "wouter";

export default function DigitalCertificate() {
  const { user, loading: authLoading } = useGoogleAuth();
  const [, setLocation] = useLocation();
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEmitenteModalOpen, setIsEmitenteModalOpen] = useState(false);
  const [isAuditDialogOpen, setIsAuditDialogOpen] = useState(false);
  const [selectedCertificadoId, setSelectedCertificadoId] = useState<number | null>(null);
  const [emitenteSearch, setEmitenteSearch] = useState("");
  const [isExtractingCert, setIsExtractingCert] = useState(false);
  const [selectedEmitente, setSelectedEmitente] = useState<{
    id: number;
    razaoSocial: string;
    cnpj: string;
  } | null>(null);
  const [formData, setFormData] = useState({
    nome: "",
    cnpj: "",
    razaoSocial: "",
    dataEmissao: "",
    dataValidade: "",
    serialNumber: "",
    emissor: "",
    senha: "",
    arquivoBase64: "",
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Queries
  const { data: certificados, isLoading, refetch } = trpc.certificados.list.useQuery({});
  const { data: emitentes = [] } = trpc.emitentes.list.useQuery();
  const { data: auditLog } = trpc.certificados.auditLog.useQuery(
    { certificadoId: selectedCertificadoId! },
    { enabled: !!selectedCertificadoId }
  );

  // Mutation para extrair dados do certificado
  const extractCertMutation = trpc.certificados.extractInfo.useMutation({
    onSuccess: (data) => {
      setFormData((prev) => ({
        ...prev,
        cnpj: data.cnpj || prev.cnpj,
        razaoSocial: data.razaoSocial || prev.razaoSocial,
        dataEmissao: data.dataEmissao || prev.dataEmissao,
        dataValidade: data.dataValidade || prev.dataValidade,
        serialNumber: data.serialNumber || prev.serialNumber,
        emissor: data.emissor || prev.emissor,
      }));
      toast.success("Dados extraídos do certificado com sucesso!");
      setIsExtractingCert(false);
    },
    onError: (error) => {
      toast.error(`Erro ao extrair dados: ${error.message}`);
      setIsExtractingCert(false);
    },
  });

  // Mutations
  const createMutation = trpc.certificados.create.useMutation({
    onSuccess: () => {
      toast.success("Certificado cadastrado com sucesso!");
      setIsDialogOpen(false);
      resetForm();
      refetch();
    },
    onError: (error) => {
      toast.error(`Erro ao cadastrar: ${error.message}`);
    },
  });

  const deleteMutation = trpc.certificados.delete.useMutation({
    onSuccess: () => {
      toast.success("Certificado removido com sucesso!");
      refetch();
    },
    onError: (error) => {
      toast.error(`Erro ao remover: ${error.message}`);
    },
  });

  useEffect(() => {
    if (!authLoading && !user) {
      window.location.href = "/login";
    }
  }, [user, authLoading]);

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }


  const resetForm = () => {
    setFormData({
      nome: "",
      cnpj: "",
      razaoSocial: "",
      dataEmissao: "",
      dataValidade: "",
      serialNumber: "",
      emissor: "",
      senha: "",
      arquivoBase64: "",
    });
    setSelectedEmitente(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".pfx") && !file.name.toLowerCase().endsWith(".p12")) {
      toast.error("Apenas arquivos .pfx ou .p12 são permitidos");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      setFormData((prev) => ({
        ...prev,
        arquivoBase64: base64,
        nome: prev.nome || file.name.replace(/\.(pfx|p12)$/i, ""),
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleExtractCertData = () => {
    if (!formData.arquivoBase64) {
      toast.error("Selecione o arquivo do certificado primeiro");
      return;
    }
    if (!formData.senha) {
      toast.error("Informe a senha do certificado para extrair os dados");
      return;
    }
    setIsExtractingCert(true);
    extractCertMutation.mutate({
      arquivoBase64: formData.arquivoBase64,
      senha: formData.senha,
    });
  };

  const handleSubmit = () => {
    if (!selectedEmitente) {
      toast.error("Selecione um emitente");
      return;
    }
    if (!formData.nome) {
      toast.error("Informe um nome para o certificado");
      return;
    }
    if (!formData.arquivoBase64) {
      toast.error("Selecione o arquivo do certificado (.pfx)");
      return;
    }
    if (!formData.senha) {
      toast.error("Informe a senha do certificado");
      return;
    }

    createMutation.mutate({
      emitenteId: selectedEmitente.id,
      nome: formData.nome,
      cnpj: formData.cnpj || undefined,
      razaoSocial: formData.razaoSocial || undefined,
      dataEmissao: formData.dataEmissao || undefined,
      dataValidade: formData.dataValidade || undefined,
      serialNumber: formData.serialNumber || undefined,
      emissor: formData.emissor || undefined,
      arquivoBase64: formData.arquivoBase64,
      senha: formData.senha,
    });
  };

  const handleDelete = (id: number, nome: string) => {
    if (confirm(`Tem certeza que deseja remover o certificado "${nome}"?`)) {
      deleteMutation.mutate({ id });
    }
  };

  const getValidadeStatus = (dataValidade: string | Date | null) => {
    if (!dataValidade) return { status: "unknown", text: "Sem data", color: "text-gray-500" };
    
    const validade = typeof dataValidade === 'string' ? parseISO(dataValidade) : new Date(dataValidade);
    const diasRestantes = differenceInDays(validade, new Date());
    
    if (diasRestantes < 0) {
      return { status: "expired", text: "Vencido", color: "text-red-600 bg-red-50" };
    } else if (diasRestantes <= 30) {
      return { status: "warning", text: `${diasRestantes} dias`, color: "text-orange-600 bg-orange-50" };
    } else if (diasRestantes <= 90) {
      return { status: "attention", text: `${diasRestantes} dias`, color: "text-yellow-600 bg-yellow-50" };
    }
    return { status: "ok", text: `${diasRestantes} dias`, color: "text-green-600 bg-green-50" };
  };

  const openAuditLog = (certificadoId: number) => {
    setSelectedCertificadoId(certificadoId);
    setIsAuditDialogOpen(true);
  };

  const handleSelectEmitente = (emitente: { id: number; razaoSocial: string; cnpj: string }) => {
    setSelectedEmitente(emitente);
    setIsEmitenteModalOpen(false);
    setEmitenteSearch("");
  };

  // Filtrar emitentes pela busca
  const filteredEmitentes = emitentes?.filter((e: any) => {
    const search = emitenteSearch.toLowerCase();
    return (
      e.razaoSocial?.toLowerCase().includes(search) ||
      e.nomeFantasia?.toLowerCase().includes(search) ||
      e.cnpj?.includes(search)
    );
  }) || [];

  return (
    <div className="p-4 lg:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Shield className="h-8 w-8 text-orange-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Certificados Digitais</h1>
            <p className="text-sm text-gray-500">Gerencie os certificados A1 dos emitentes</p>
          </div>
        </div>
        <Button onClick={() => setIsDialogOpen(true)} className="bg-orange-600 hover:bg-orange-700">
          <Plus className="h-4 w-4 mr-2" />
          Novo Certificado
        </Button>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-lg border shadow-sm">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="font-semibold">Nome</TableHead>
              <TableHead className="font-semibold">Emitente</TableHead>
              <TableHead className="font-semibold">CNPJ</TableHead>
              <TableHead className="font-semibold">Validade</TableHead>
              <TableHead className="font-semibold">Status</TableHead>
              <TableHead className="font-semibold">Cadastrado em</TableHead>
              <TableHead className="font-semibold text-center">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                  Carregando...
                </TableCell>
              </TableRow>
            ) : !certificados || certificados.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                  <FileCheck className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>Nenhum certificado cadastrado</p>
                </TableCell>
              </TableRow>
            ) : (
              certificados.map((cert: any) => {
                const validadeStatus = getValidadeStatus(cert.dataValidade);
                return (
                  <TableRow key={cert.id} className="hover:bg-gray-50">
                    <TableCell className="font-medium">{cert.nome}</TableCell>
                    <TableCell>{cert.emitenteNome || "-"}</TableCell>
                    <TableCell className="font-mono text-sm">{cert.cnpj || "-"}</TableCell>
                    <TableCell>
                      {cert.dataValidade
                        ? format(typeof cert.dataValidade === 'string' ? parseISO(cert.dataValidade) : new Date(cert.dataValidade), "dd/MM/yyyy", { locale: ptBR })
                        : "-"}
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${validadeStatus.color}`}>
                        {validadeStatus.text}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {cert.criadoEm
                        ? format(typeof cert.criadoEm === 'string' ? parseISO(cert.criadoEm) : new Date(cert.criadoEm), "dd/MM/yyyy", { locale: ptBR })
                        : "-"}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openAuditLog(cert.id)}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        >
                          <History className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(cert.id, cert.nome)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Dialog Novo Certificado */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Shield className="h-5 w-5 text-orange-600" />
              Novo Certificado Digital
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Seleção de Emitente */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Emitente *
              </Label>
              {selectedEmitente ? (
                <div className="flex items-center gap-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="flex-1">
                    <p className="font-medium text-sm">{selectedEmitente.razaoSocial}</p>
                    <p className="text-xs text-gray-600 font-mono">{selectedEmitente.cnpj}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedEmitente(null)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => setIsEmitenteModalOpen(true)}
                  className="w-full justify-start text-gray-500"
                >
                  <Search className="h-4 w-4 mr-2" />
                  Selecionar emitente...
                </Button>
              )}
            </div>

            {/* Upload do Arquivo */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold flex items-center gap-2">
                <Upload className="h-4 w-4" />
                Arquivo do Certificado (.pfx ou .p12) *
              </Label>
              <div className="flex gap-2">
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept=".pfx,.p12"
                  onChange={handleFileChange}
                  className="flex-1"
                />
                {formData.arquivoBase64 && (
                  <div className="flex items-center gap-1 px-3 py-2 bg-green-50 border border-green-200 rounded-md">
                    <Check className="h-4 w-4 text-green-600" />
                    <span className="text-xs text-green-700 font-medium">Arquivo carregado</span>
                  </div>
                )}
              </div>
            </div>

            {/* Senha do Certificado */}
            <div className="space-y-2">
              <Label htmlFor="senha" className="text-sm font-semibold">Senha do Certificado *</Label>
              <div className="flex gap-2">
                <Input
                  id="senha"
                  type="password"
                  value={formData.senha}
                  onChange={(e) => setFormData({ ...formData, senha: e.target.value })}
                  placeholder="Digite a senha do certificado"
                  className="flex-1"
                />
                <Button
                  onClick={handleExtractCertData}
                  disabled={!formData.arquivoBase64 || !formData.senha || isExtractingCert}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {isExtractingCert ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Extraindo...
                    </>
                  ) : (
                    <>
                      <FileCheck className="h-4 w-4 mr-2" />
                      Extrair Dados
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                Clique em "Extrair Dados" para preencher automaticamente os campos abaixo
              </p>
            </div>

            <div className="border-t pt-4">
              <h3 className="text-sm font-semibold mb-3 text-gray-700">Informações do Certificado</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nome">Nome/Identificação *</Label>
                  <Input
                    id="nome"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    placeholder="Ex: Certificado Matriz"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cnpj">CNPJ</Label>
                  <Input
                    id="cnpj"
                    value={formData.cnpj}
                    onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })}
                    placeholder="00.000.000/0000-00"
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="razaoSocial">Razão Social</Label>
                  <Input
                    id="razaoSocial"
                    value={formData.razaoSocial}
                    onChange={(e) => setFormData({ ...formData, razaoSocial: e.target.value })}
                    placeholder="Razão social do titular"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="dataEmissao">Data de Emissão</Label>
                  <Input
                    id="dataEmissao"
                    type="date"
                    value={formData.dataEmissao}
                    onChange={(e) => setFormData({ ...formData, dataEmissao: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="dataValidade">Data de Validade</Label>
                  <Input
                    id="dataValidade"
                    type="date"
                    value={formData.dataValidade}
                    onChange={(e) => setFormData({ ...formData, dataValidade: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="serialNumber">Número de Série</Label>
                  <Input
                    id="serialNumber"
                    value={formData.serialNumber}
                    onChange={(e) => setFormData({ ...formData, serialNumber: e.target.value })}
                    placeholder="Serial number do certificado"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="emissor">Emissor (CA)</Label>
                  <Input
                    id="emissor"
                    value={formData.emissor}
                    onChange={(e) => setFormData({ ...formData, emissor: e.target.value })}
                    placeholder="Ex: AC SERASA RFB v5"
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setIsDialogOpen(false); resetForm(); }}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} className="bg-orange-600 hover:bg-orange-700">
              <Check className="h-4 w-4 mr-2" />
              Cadastrar Certificado
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Seleção de Emitente */}
      <Dialog open={isEmitenteModalOpen} onOpenChange={setIsEmitenteModalOpen}>
        <DialogContent className="max-w-2xl max-h-[70vh]">
          <DialogHeader>
            <DialogTitle>Selecionar Emitente</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Buscar por razão social, nome fantasia ou CNPJ..."
                value={emitenteSearch}
                onChange={(e) => setEmitenteSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="border rounded-lg max-h-96 overflow-y-auto">
              {filteredEmitentes.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Building2 className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>Nenhum emitente encontrado</p>
                </div>
              ) : (
                <div className="divide-y">
                  {filteredEmitentes.map((emitente: any) => (
                    <button
                      key={emitente.id}
                      onClick={() => handleSelectEmitente(emitente)}
                      className="w-full text-left p-3 hover:bg-gray-50 transition-colors"
                    >
                      <p className="font-medium text-sm">{emitente.razaoSocial}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-600 font-mono">{emitente.cnpj}</span>
                        {emitente.nomeFantasia && (
                          <span className="text-xs text-gray-500">• {emitente.nomeFantasia}</span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Audit Log */}
      <Dialog open={isAuditDialogOpen} onOpenChange={setIsAuditDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[70vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Histórico de Alterações
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {!auditLog || auditLog.length === 0 ? (
              <p className="text-center py-8 text-gray-500">Nenhum registro de auditoria</p>
            ) : (
              auditLog.map((log: any) => (
                <div key={log.id} className="border rounded-lg p-3 bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium">{log.acao}</p>
                      <p className="text-xs text-gray-600 mt-1">{log.detalhes}</p>
                    </div>
                    <div className="text-right text-xs text-gray-500">
                      <p>{log.usuarioNome || "Sistema"}</p>
                      <p>
                        {log.alteradoEm
                          ? format(typeof log.alteradoEm === 'string' ? parseISO(log.alteradoEm) : new Date(log.alteradoEm), "dd/MM/yyyy HH:mm", { locale: ptBR })
                          : "-"}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
